import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Alert,
    AlertIcon,
    Flex,
    Stack,
    Tag,
    Text,
} from '@chakra-ui/react';
import startCase from 'lodash/startCase';
import { useMemo } from 'react';

import { useAccountGroupOptimizerDetails } from '@/cip/api/optimization';
import { PageSpinner } from '@/cip/widgets/PageSpinner';
import { formatCurrency } from '@/utils/number-formatter';

import { type AccountGroupMember, toAccountGroupMembers } from './accountGroupAllocation';
import { AccountAllocationTables } from './OptimizerAllocationTables';

/**
 * The other accounts of a portfolio-level account group, each expandable to the same allocation
 * tables the Advanced view shows for the debugged account.
 */
export const AccountGroupMembers = ({
    optimizationId,
    accountGroupGuid,
    pId,
}: {
    optimizationId: number;
    accountGroupGuid: string;
    /** The account being debugged; its tables are already shown above, so it is left out. */
    pId: string;
}) => {
    const { data, isPending, isError, error } = useAccountGroupOptimizerDetails({ optimizationId, accountGroupGuid });
    const siblings = useMemo(() => toAccountGroupMembers(data ?? []).filter((m) => m.pId !== pId), [data, pId]);

    return (
        <Stack gap={1}>
            <Flex align="center" gap={3}>
                <Text fontSize="lg" fontWeight="bold">
                    Other accounts in group
                </Text>
                <Tag colorScheme="teal">{accountGroupGuid}</Tag>
            </Flex>
            {isPending ? (
                <PageSpinner />
            ) : isError ? (
                <Alert status="error">
                    <AlertIcon />
                    Failed to load account group details: {error.message}
                </Alert>
            ) : siblings.length === 0 ? (
                <Text>No other accounts in this group</Text>
            ) : (
                <Accordion allowMultiple>
                    {siblings.map((member) => (
                        <MemberAccordionItem key={member.pId} member={member} />
                    ))}
                </Accordion>
            )}
        </Stack>
    );
};

const MemberAccordionItem = ({ member }: { member: AccountGroupMember }) => (
    <AccordionItem>
        {({ isExpanded }) => (
            <>
                <AccordionButton background="whiteAlpha.50">
                    <Flex as="span" flex="1" textAlign="left" align="center" gap={3}>
                        <Text fontWeight="bold">{member.pId}</Text>
                        {member.portfolioGuid && (
                            <Text fontSize="sm" color="gray.400">
                                {member.portfolioGuid}
                            </Text>
                        )}
                        {member.accountType && <Tag size="sm">{startCase(member.accountType)}</Tag>}
                        {member.nav !== undefined && <Tag size="sm">NAV {formatCurrency(member.nav)}</Tag>}
                    </Flex>
                    <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                    {/* Chakra keeps collapsed panels mounted, so without this guard every account's tables render */}
                    {isExpanded && <AccountAllocationTables details={member.details} />}
                </AccordionPanel>
            </>
        )}
    </AccordionItem>
);
